import bhLogo from "../../assets/landing/bh_logo_round.svg";
import btLogo from "../../assets/landing/bt_logo_round.svg";
import markBh from "../../assets/landing/mark-bh.svg";
import markBt from "../../assets/landing/mark-bt.svg";
import markSm from "../../assets/landing/mark-sm.svg";
import markSt from "../../assets/landing/mark-st.svg";
import markTm from "../../assets/landing/mark-tm.svg";
import markZb from "../../assets/landing/mark-zb.svg";
import mastheadBh from "../../assets/landing/masthead-bh.svg";
import mastheadBt from "../../assets/landing/masthead-bt.svg";
import mastheadSm from "../../assets/landing/masthead-sm.svg";
import mastheadSt from "../../assets/landing/masthead-st.svg";
import mastheadTm from "../../assets/landing/masthead-tm.webp";
import mastheadZb from "../../assets/landing/masthead-zb.svg";
import slideBhLandscape from "../../assets/landing/slide-bh-landscape.webp";
import slideBhPortrait from "../../assets/landing/slide-bh-portrait.webp";
import slideBtLandscape from "../../assets/landing/slide-bt-landscape.webp";
import slideBtPortrait from "../../assets/landing/slide-bt-portrait.webp";
import slideSmLandscape from "../../assets/landing/slide-sm-landscape.webp";
import slideSmPortrait from "../../assets/landing/slide-sm-portrait.webp";
import slideStLandscape from "../../assets/landing/slide-st-landscape.webp";
import slideStPortrait from "../../assets/landing/slide-st-portrait.webp";
import slideTmLandscape from "../../assets/landing/slide-tm-landscape.webp";
import slideTmPortrait from "../../assets/landing/slide-tm-portrait.webp";
import slideZbLandscape from "../../assets/landing/slide-zb-landscape.webp";
import slideZbPortrait from "../../assets/landing/slide-zb-portrait.webp";
import smLogo from "../../assets/landing/sm_logo_round.svg";
import stLogo from "../../assets/landing/st_logo_round.svg";
import tmLogo from "../../assets/landing/tm_logo_round.svg";
import zbLogo from "../../assets/landing/zb_logo_round.svg";

export const SUPPORTED_LANGS = ["en", "zh"] as const;
export type LandingLang = (typeof SUPPORTED_LANGS)[number];

// The A/B arms: the 1.0 page is the control, the two 2.0 layouts are A and B.
export type LandingVariant = "control" | "showcase" | "plans";

/** The 2.0 layouts; the control is the 1.0 page and never renders through LandingPage. */
export type LandingPageVariant = Exclude<LandingVariant, "control">;

/** Route per arm and language; the language switch keeps the visitor in their arm. */
export const LANDING_PATHS: Record<
  LandingVariant,
  Record<LandingLang, string>
> = {
  control: { en: "/", zh: "/landing/zh" },
  showcase: { en: "/landing/sub", zh: "/zh/landing/sub" },
  plans: { en: "/landing/plans", zh: "/zh/landing/plans" },
};

export type LandingPubId = "st" | "bt" | "zb" | "sm" | "bh" | "tm";

interface LandingPub {
  id: LandingPubId;
  /** Icon from the design's pub-logo component, used by the 2.0 publication selector. */
  mark: string;
  /** Round icon used by the 1.0 control page. */
  logo: string;
  /** Wordmark, used in the "Read E-Paper" dropdown. */
  masthead: string;
  /** Intrinsic masthead size; the dropdown reserves this to avoid layout shift. */
  mastheadWidth: number;
  mastheadHeight: number;
}

// Masthead sizes are the supplied SVGs' own viewBox values.
const PUBS: Record<LandingPubId, LandingPub> = {
  st: {
    id: "st",
    logo: stLogo,
    mark: markSt,
    masthead: mastheadSt,
    mastheadWidth: 230,
    mastheadHeight: 18,
  },
  bt: {
    id: "bt",
    logo: btLogo,
    mark: markBt,
    masthead: mastheadBt,
    mastheadWidth: 195,
    mastheadHeight: 18,
  },
  zb: {
    id: "zb",
    logo: zbLogo,
    mark: markZb,
    masthead: mastheadZb,
    mastheadWidth: 117,
    mastheadHeight: 38,
  },
  sm: {
    id: "sm",
    logo: smLogo,
    mark: markSm,
    masthead: mastheadSm,
    mastheadWidth: 125,
    mastheadHeight: 37,
  },
  bh: {
    id: "bh",
    logo: bhLogo,
    mark: markBh,
    masthead: mastheadBh,
    mastheadWidth: 141,
    mastheadHeight: 24,
  },
  tm: {
    // The supplied TM wordmark is bitmap art; served as a 3x raster instead of a 273KB SVG wrapper.
    id: "tm",
    logo: tmLogo,
    mark: markTm,
    masthead: mastheadTm,
    mastheadWidth: 149,
    mastheadHeight: 32,
  },
};

/** Display order: the design's on English; the Chinese papers first, Zaobao leading, on Chinese. */
const PUB_ORDER: Record<LandingLang, readonly LandingPubId[]> = {
  en: ["st", "bt", "zb", "sm", "bh", "tm"],
  zh: ["zb", "sm", "st", "bt", "bh", "tm"],
};

/** The 1.0 control page's grid, frozen to the order prd serves; the 2.0 pages reorder freely. */
const CONTROL_PUB_ORDER: Record<LandingLang, readonly LandingPubId[]> = {
  en: ["st", "zb", "sm", "tm", "bt", "bh"],
  zh: ["sm", "zb", "st", "bt", "bh", "tm"],
};

export const CONTROL_PUBLICATIONS: Record<LandingLang, readonly LandingPub[]> =
  {
    en: CONTROL_PUB_ORDER.en.map((id) => PUBS[id]),
    zh: CONTROL_PUB_ORDER.zh.map((id) => PUBS[id]),
  };

export const PUBLICATIONS: Record<LandingLang, readonly LandingPub[]> = {
  en: PUB_ORDER.en.map((id) => PUBS[id]),
  zh: PUB_ORDER.zh.map((id) => PUBS[id]),
};

export function getPub(id: LandingPubId): LandingPub {
  return PUBS[id];
}

/** BCP 47 tag of each publication's language, for `lang` attributes. */
export const PUB_LANG: Record<LandingPubId, string> = {
  st: "en",
  bt: "en",
  zb: "zh-Hans",
  sm: "zh-Hans",
  bh: "ms",
  tm: "ta",
};

export type LandingPlan = "subscription" | "single";

// Our own prices, not an E-Shop feed; wording stays in i18n because CN reorders it.
export const PLAN_PRICES: Record<LandingPlan, Record<LandingPubId, string>> = {
  subscription: {
    st: "$9.90",
    bt: "$9.90",
    zb: "$9.90",
    sm: "$9.90",
    bh: "$9.90",
    tm: "$9.90",
  },
  single: {
    st: "$0.60",
    bt: "$0.60",
    zb: "$0.60",
    sm: "$0.60",
    bh: "$0.60",
    tm: "$0.60",
  },
};

// The Zaobao News Tablet bundle, CN page only; one price, no per-publication variant.
export const TABLET_PLAN_PRICE = "$25.90";

// Six slides; two Figma frames are mislabelled, so this follows the artwork.
export type ShowcasePubId = LandingPubId;

/** Showcase order per page: the design's on English, the Chinese papers first on Chinese. */
export const SHOWCASE_PUBS: Record<LandingLang, readonly ShowcasePubId[]> = {
  en: ["st", "zb", "sm", "bt", "bh", "tm"],
  zh: ["zb", "sm", "st", "bt", "bh", "tm"],
};

/** Each page opens on its own publication, in the showcase and in the plan cards alike. */
export const DEFAULT_PUB: Record<LandingLang, ShowcasePubId> = {
  en: "st",
  zh: "zb",
};

/** Plan card tabs per page; the Chinese page offers the two Chinese papers only. */
export const PLAN_PUBS: Record<LandingLang, readonly LandingPubId[]> = {
  en: PUB_ORDER.en,
  zh: ["zb", "sm"],
};

interface ShowcaseSlide {
  landscape: string;
  portrait: string;
}

/** Rendered sizes; sources are 2x. */
export const SLIDE_LANDSCAPE = { width: 474, height: 336 };
export const SLIDE_PORTRAIT = { width: 212, height: 381 };

export const SHOWCASE_SLIDES: Record<ShowcasePubId, ShowcaseSlide> = {
  st: { landscape: slideStLandscape, portrait: slideStPortrait },
  zb: { landscape: slideZbLandscape, portrait: slideZbPortrait },
  sm: { landscape: slideSmLandscape, portrait: slideSmPortrait },
  bt: { landscape: slideBtLandscape, portrait: slideBtPortrait },
  bh: { landscape: slideBhLandscape, portrait: slideBhPortrait },
  tm: { landscape: slideTmLandscape, portrait: slideTmPortrait },
};
